/**
 * Cliente HTTP do CRM.
 *
 * Responsabilidades:
 *  - enviar o cookie de sessão (`credentials: 'include'`);
 *  - anexar o token CSRF nas mutações (double-submit);
 *  - traduzir o erro padronizado da API em `ApiError` com `details` preservados
 *    (a UI precisa de `missingLabels` para explicar por que o Kanban bloqueou);
 *  - propagar `Idempotency-Key` quando a operação for crítica.
 */

import { traduzirCodigo, type ApiErrorBody } from '@crm/contracts';

const BASE = '/api/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly correlationId: string;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.correlationId = body.correlationId;
  }

  /**
   * A FRASE QUE VAI PARA A TELA (§20.3).
   *
   * `traduzirCodigo()` existia no contrato e nenhuma tela o alcançava: a
   * interface mostrava `error.message` cru, e quando o servidor responde só com
   * o código — ou com algo que parece técnico — era isso que a pessoa lia.
   *
   * A mensagem do SERVIDOR vence quando é específica: ela conhece o caso ("já
   * existe uma empresa com este CNPJ" é melhor do que qualquer frase genérica
   * sobre conflito). Sem ela, entra a tradução do código, que diz o que fazer.
   */
  get mensagemHumana(): string {
    const doServidor = this.message.trim();
    const pareceCodigo = /^[A-Z][A-Z0-9_]{3,}$/.test(doServidor);
    const pareceTecnica =
      /\bat\s+\w+\.\w+\s*\(|\/[a-z]+\/[a-z]+\.[jt]s|\bSELECT\b.+\bFROM\b|prisma\./i.test(
        doServidor,
      );

    if (doServidor !== '' && !pareceCodigo && !pareceTecnica) return doServidor;
    return traduzirCodigo(this.code);
  }

  /** `true` quando a sessão expirou — a UI redireciona para o login. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * Erros de validação por campo, prontos para o formulário. (BKC-005)
   *
   * A versão anterior procurava `issue.field`, chave que a API NUNCA emite: o
   * validador produz `instancePath` (`/accountId`, `/contacts/0/email`). O
   * getter portanto devolvia sempre uma lista vazia, e todo erro de validação
   * caía na mensagem genérica "Dados inválidos" — o campo culpado ficava
   * invisível mesmo quando o servidor dizia exatamente qual era.
   */
  get fieldIssues(): Array<{ field: string; message: string }> {
    const details = this.details as
      | {
          issues?: Array<{
            instancePath?: string;
            field?: string;
            message?: string;
            params?: { issue?: { path?: Array<string | number>; message?: string } };
          }>;
        }
      | undefined;

    if (details?.issues === undefined) return [];

    return details.issues.flatMap((issue) => {
      const field = fieldFromIssue(issue);
      const message = issue.params?.issue?.message ?? issue.message;
      if (field === null || message === undefined) return [];
      return [{ field, message: humanizeIssueMessage(message) }];
    });
  }

  /** Mensagens de campo agrupadas, para o resumo acessível do formulário. */
  get fieldIssueSummary(): Array<{ field: string; message: string }> {
    const seen = new Set<string>();
    return this.fieldIssues.filter((issue) => {
      const key = `${issue.field}|${issue.message}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /** `true` quando outra pessoa alterou o registro antes (409 de concorrência). */
  get isConcurrentModification(): boolean {
    return this.code === 'CONCURRENT_MODIFICATION';
  }
}

/**
 * Extrai o nome do campo de um problema de validação.
 *
 * Ordem de preferência:
 *   1. `params.issue.path` — o caminho estruturado do Zod, o mais confiável;
 *   2. `instancePath` — o ponteiro JSON do validador (`/a/0/b`);
 *   3. `field`, caso a API venha a emiti-lo no futuro.
 *
 * Índices de array são preservados (`contacts.0.email`) para que o formulário
 * consiga apontar a linha certa de uma lista.
 */
function fieldFromIssue(issue: {
  instancePath?: string;
  field?: string;
  params?: { issue?: { path?: Array<string | number> } };
}): string | null {
  const path = issue.params?.issue?.path;
  if (Array.isArray(path) && path.length > 0) return path.join('.');

  if (typeof issue.instancePath === 'string' && issue.instancePath !== '') {
    return issue.instancePath.replace(/^\//, '').split('/').join('.');
  }

  return issue.field ?? null;
}

/**
 * Traduz as mensagens genéricas do validador.
 *
 * As mensagens de NEGÓCIO (duplicidade de CNPJ, pendências de transição, vínculo
 * obrigatório) já vêm em português e passam intactas — traduzir por cima
 * pioraria. Só o vocabulário técnico do Zod, que é fixo e em inglês, é mapeado.
 */
function humanizeIssueMessage(message: string): string {
  const exatas: Record<string, string> = {
    Required: 'Campo obrigatório.',
    'Invalid input': 'Valor inválido.',
    'Invalid uuid': 'Identificador inválido.',
    'Invalid email': 'E-mail inválido.',
    'Invalid url': 'Endereço inválido.',
    'Invalid date': 'Data inválida.',
    'Unrecognized key(s) in object': 'Campo não reconhecido.',
  };
  if (exatas[message] !== undefined) return exatas[message];

  const tamanhoMax = /^String must contain at most (\d+) character/.exec(message);
  if (tamanhoMax !== null) return `Use no máximo ${tamanhoMax[1]} caracteres.`;

  const tamanhoMin = /^String must contain at least (\d+) character/.exec(message);
  if (tamanhoMin !== null) return `Use ao menos ${tamanhoMin[1]} caracteres.`;

  const numeroMax = /^Number must be less than or equal to (\S+)/.exec(message);
  if (numeroMax !== null) return `Informe um valor de até ${numeroMax[1]}.`;

  const numeroMin = /^Number must be greater than or equal to (\S+)/.exec(message);
  if (numeroMin !== null) return `Informe um valor a partir de ${numeroMin[1]}.`;

  if (/^Expected .+, received/.test(message)) return 'Tipo de valor inválido.';
  if (/^Invalid enum value/.test(message)) return 'Selecione uma das opções válidas.';
  if (/^Unrecognized key/.test(message)) return 'Campo não reconhecido.';

  return message;
}

/** Lê o cookie CSRF (legível por JS de propósito). */
function readCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)crm_csrf=([^;]+)/);
  if (match?.[1] === undefined) return null;
  const raw = decodeURIComponent(match[1]);
  // O cookie é assinado: `s:<valor>.<assinatura>`.
  const withoutPrefix = raw.startsWith('s:') ? raw.slice(2) : raw;
  return withoutPrefix.split('.')[0] ?? null;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Aviso de que o servidor passou a exigir aceite de documento contratual.
 *
 * O CASO QUE ISTO RESOLVE: a pessoa está trabalhando, uma versão nova dos Termos
 * entra em vigor, e a partir daí TODA requisição dela é recusada com
 * `LEGAL_ACCEPTANCE_REQUIRED`. Sem este aviso, a tela onde ela está continuaria
 * montada enchendo de erro, e o desvio para o aceite só aconteceria se alguém
 * recarregasse a página — que é justamente o que ninguém faz ao ver um erro.
 *
 * É um registro de função, e não um `window.location` daqui, por duas razões: o
 * cliente HTTP não deveria conhecer as rotas da aplicação, e uma navegação
 * forçada perderia o trabalho não salvo sem chance de aviso. Quem se registra é
 * a sessão, que reconsulta `/auth/me` — e o desvio passa a ser o mesmo de sempre.
 */
type AoExigirAceite = () => void;
let aoExigirAceite: AoExigirAceite | null = null;

export function registrarAvisoDeAceitePendente(fn: AoExigirAceite | null): void {
  aoExigirAceite = fn;
}

/**
 * CERES-ADMIN-PLANOS-001 — aviso de que o servidor NEGOU uma capacidade.
 *
 * A lista de capacidades da sessão vem de `/auth/me` e só era lida ao carregar
 * a página. Quando a plataforma revoga um recurso, o gate passa a negar na
 * requisição seguinte, mas o botão continuava na tela até alguém recarregar.
 * Com este aviso, `FEATURE_DISABLED` faz a sessão se reconsultar — mesmo padrão
 * do aviso de aceite, e pelo mesmo motivo: o cliente HTTP não conhece a sessão.
 */
type AoNegarCapacidade = () => void;
let aoNegarCapacidade: AoNegarCapacidade | null = null;

export function registrarAvisoDeCapacidadeAlterada(fn: AoNegarCapacidade | null): void {
  aoNegarCapacidade = fn;
}

/**
 * CERES-DP-014 (ADR-060 §4) — aviso de que a organização foi BLOQUEADA por
 * inadimplência (403 com `motivo: ASSINATURA_SUSPENSA`). Mesmo padrão dos
 * avisos acima: o cliente HTTP só avisa; quem se registra mostra a tela de
 * bloqueio com o caminho para a cobrança.
 */
type AoBloquear = (desde: string | null) => void;
let aoBloquear: AoBloquear | null = null;

export function registrarAvisoDeBloqueio(fn: AoBloquear | null): void {
  aoBloquear = fn;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Chave de idempotência para operações críticas. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /**
   * Ignora o prefixo `/api/v1`.
   *
   * Só para rotas de operação (`/version`, `/health`), que existem exatamente
   * para serem consultadas quando algo deu errado — e por isso não devem mudar
   * de endereço junto com a API de negócio.
   */
  absolute?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  if (MUTATING.has(method)) {
    const csrf = readCsrfToken();
    if (csrf !== null) headers['x-csrf-token'] = csrf;
  }
  if (options.idempotencyKey !== undefined) {
    headers['idempotency-key'] = options.idempotencyKey;
  }

  // `absolute` sai do prefixo versionado. Existe para `/version` e `/health`, que
  // são rotas de OPERAÇÃO e não de negócio: elas não versionam junto com a API,
  // porque quem as consulta durante um incidente precisa que elas nunca mudem.
  const url = options.absolute === true ? path : `${BASE}${path}`;

  const response = await fetch(url, {
    method,
    headers,
    // Envia e recebe o cookie de sessão.
    credentials: 'include',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const body = (payload as ApiErrorBody | null)?.error;
    // Antes de lançar: o gate de aceite pode ter entrado em vigor no meio da
    // sessão, e quem sabe disso é só o servidor.
    if (body?.code === 'LEGAL_ACCEPTANCE_REQUIRED') aoExigirAceite?.();
    if (body?.code === 'FEATURE_DISABLED') aoNegarCapacidade?.();
    const detalhes = body?.details as { motivo?: unknown; desde?: unknown } | undefined;
    if (response.status === 403 && detalhes?.motivo === 'ASSINATURA_SUSPENSA')
      aoBloquear?.(typeof detalhes.desde === 'string' ? detalhes.desde : null);
    throw new ApiError(response.status, body ?? semCorpo(response));
  }

  return payload as T;
}

/**
 * A RESPOSTA DE ERRO QUE NÃO VEIO DA API.
 *
 * O CRM devolve SEMPRE um corpo `{error:{code,message,correlationId}}` — o
 * tratador global garante isso. Então uma resposta de erro SEM esse corpo não é
 * um erro da aplicação: é um erro de quem está no meio do caminho.
 *
 * O CASO REAL, e o motivo desta função existir
 *
 * O assistente inteligente leva de 10 s a mais de 100 s conforme o pedido —
 * interpretar é rápido, redigir um playbook não é. O `proxy_read_timeout` do
 * nginx encerra a conexão aos 60 s e devolve uma PÁGINA HTML de 504. O
 * `JSON.parse` falha, o corpo vira `null`, e a tela dizia "Não foi possível
 * concluir a operação. Tente novamente."
 *
 * Essa frase é falsa em três sentidos ao mesmo tempo: sugere que a operação
 * falhou (ela pode ter sido concluída no servidor, depois do corte), não diz o
 * que aconteceu, e manda repetir uma tentativa que vai bater no mesmo limite.
 * Quem lê conclui que o assistente está quebrado.
 *
 * Cada família de status agora diz a verdade do seu caso, e o `code` é próprio
 * para que a tela possa reagir de forma diferente a cada um.
 */
function semCorpo(response: Response): ApiErrorBody['error'] {
  const correlationId = response.headers.get('x-correlation-id') ?? 'desconhecido';

  if (response.status === 504 || response.status === 408) {
    return {
      code: 'GATEWAY_TIMEOUT',
      message:
        'O servidor demorou mais do que o limite da conexão para responder. ' +
        'A operação pode ter sido concluída mesmo assim — confira antes de repetir.',
      correlationId,
    };
  }
  if (response.status === 502 || response.status === 503) {
    return {
      code: 'GATEWAY_UNAVAILABLE',
      message: 'O servidor da aplicação não respondeu. Nada foi executado.',
      correlationId,
    };
  }
  return {
    code: 'INTERNAL_ERROR',
    message: `A resposta do servidor não pôde ser interpretada (HTTP ${response.status}).`,
    correlationId,
  };
}

/**
 * ENVIO DE ARQUIVO (§20.3).
 *
 * Separado do `request` por um detalhe que quebra silenciosamente: com
 * `FormData`, o `content-type` precisa ser montado pelo NAVEGADOR, porque ele
 * carrega a fronteira do multipart. Defini-lo à mão produz um corpo que o
 * servidor não consegue separar — e o erro que volta fala de campo faltando, e
 * não de cabeçalho.
 */
export async function upload<T>(path: string, corpo: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  const csrf = readCsrfToken();
  if (csrf !== null) headers['x-csrf-token'] = csrf;

  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: corpo,
  });

  const texto = await response.text();
  const payload: unknown = texto.length > 0 ? JSON.parse(texto) : null;

  if (!response.ok) {
    const corpoErro = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(
      response.status,
      corpoErro ?? {
        code: 'INTERNAL_ERROR',
        message: '',
        details: null,
        correlationId: '',
      },
    );
  }

  return payload as T;
}

/**
 * PEDE UM ARQUIVO e devolve o BLOB, sem baixar (CERES-038).
 *
 * A prévia do modelo é um PDF que a pessoa precisa VER, e não guardar. Baixar
 * para ver obrigaria a abrir o gerenciador de arquivos a cada ajuste de cor —
 * e deixaria uma pasta de `previa (14).pdf` atrás. O tratamento de erro é o
 * mesmo do resto do cliente: a falha vira `ApiError`.
 */
export async function pedirArquivo(path: string, corpo?: unknown): Promise<Blob> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const csrf = readCsrfToken();
  if (csrf !== null) headers['x-csrf-token'] = csrf;

  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(corpo ?? {}),
  });

  if (!response.ok) {
    const texto = await response.text();
    let corpoErro: ApiErrorBody['error'] | undefined;
    try {
      corpoErro = (JSON.parse(texto) as ApiErrorBody | null)?.error;
    } catch {
      corpoErro = undefined;
    }
    throw new ApiError(
      response.status,
      corpoErro ?? { code: 'INTERNAL_ERROR', message: '', details: null, correlationId: '' },
    );
  }

  return response.blob();
}

/**
 * BAIXA UM ARQUIVO da API, com a sessão e o tratamento de erro de sempre.
 *
 * `window.open` na URL seria mais curto e erraria em dois pontos: a resposta de
 * erro (403 de quem não tem `export:run`, por exemplo) abriria uma aba com JSON
 * cru na cara da pessoa, e o download não passaria pelo mesmo caminho de sessão
 * do resto do cliente. Aqui a falha vira `ApiError`, como em qualquer chamada.
 */
export async function baixar(path: string, nomeSugerido: string): Promise<void> {
  const response = await fetch(`${BASE}${path}`, { credentials: 'include' });

  if (!response.ok) {
    const texto = await response.text();
    let corpoErro: ApiErrorBody['error'] | undefined;
    try {
      corpoErro = (JSON.parse(texto) as ApiErrorBody | null)?.error;
    } catch {
      corpoErro = undefined;
    }
    throw new ApiError(
      response.status,
      corpoErro ?? { code: 'INTERNAL_ERROR', message: '', details: null, correlationId: '' },
    );
  }

  /*
   * O nome vem do `content-disposition` quando o servidor manda um.
   *
   * Ele já sabe o relatório e o período; repetir essa lógica aqui criaria dois
   * nomes possíveis para o mesmo arquivo, e o do servidor é o que aparece na
   * auditoria de quem exportou o quê.
   */
  const disposition = response.headers.get('content-disposition') ?? '';
  const doServidor = /filename="([^"]+)"/.exec(disposition)?.[1];

  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = doServidor ?? nomeSugerido;
  a.click();
  URL.revokeObjectURL(url);
}

export const api = {
  upload,
  baixar,
  pedirArquivo,
  get: <T>(path: string, signal?: AbortSignal, options?: { absolute?: boolean }): Promise<T> =>
    request<T>(path, {
      ...(signal === undefined ? {} : { signal }),
      ...(options?.absolute === true ? { absolute: true } : {}),
    }),
  post: <T>(path: string, body?: unknown, idempotencyKey?: string): Promise<T> =>
    request<T>(path, {
      method: 'POST',
      ...(body !== undefined ? { body } : {}),
      ...(idempotencyKey !== undefined ? { idempotencyKey } : {}),
    }),
  patch: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'PATCH', body }),
  // `PUT` para substituição completa de um recurso subordinado — hoje, o vínculo
  // do contato com a empresa.
  put: <T>(path: string, body: unknown): Promise<T> => request<T>(path, { method: 'PUT', body }),
  /**
   * `DELETE`, com corpo OPCIONAL.
   *
   * O corpo existe porque remover o próprio segundo fator exige a senha
   * (BKC-012): sem ela, uma sessão momentaneamente acessível derrubaria a
   * proteção com um clique. A alternativa — um `POST /auth/mfa/disable` — seria
   * um verbo errado para descrever a operação.
   */
  delete: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'DELETE', ...(body === undefined ? {} : { body }) }),
};

/** Monta querystring ignorando valores vazios — evita `?status=&q=` na URL. */
export function toQuery(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query.length > 0 ? `?${query}` : '';
}

/** Chave de idempotência para uma submissão de formulário. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
